/**
 * Hints.jsx — Mouse-following hint delegator (#142).
 *
 * Mounted once at the app root. Listens for pointer events on the
 * document and shows the singleton MouseTooltip whenever the cursor is
 * over an element with a `data-hint` attribute (and the user's hint
 * intensity allows it).
 *
 * Markup contract:
 *   <button data-hint="Refresh data" data-hint-level="critical">↻</button>
 *
 *   • data-hint        — the hint text (\n preserved)
 *   • data-hint-level  — 'critical' | 'normal' (default normal)
 *
 * Off / Low / High intensity is honoured via shouldShow().
 */
import { onMount, onCleanup } from 'solid-js';
import { tooltip } from './MouseTooltip.jsx';
import { shouldShow, hints } from '../lib/hintsStore.js';

const origSetAttribute = typeof Element !== 'undefined' ? Element.prototype.setAttribute : null;
const origRemoveAttribute = typeof Element !== 'undefined' ? Element.prototype.removeAttribute : null;
const origGetAttribute = typeof Element !== 'undefined' ? Element.prototype.getAttribute : null;

export function applyRedfurTitle(el, rawValue) {
  if (!el || el.nodeType !== 1 || !origSetAttribute || !origRemoveAttribute) return;
  if (origGetAttribute.call(el, 'title') !== null) {
    origRemoveAttribute.call(el, 'title');
  }
  const text = String(rawValue ?? '').trim();
  if (!text) {
    origRemoveAttribute.call(el, 'data-redfur-title');
    if (origGetAttribute.call(el, 'data-hint-source') === 'title') {
      origRemoveAttribute.call(el, 'data-hint');
      origRemoveAttribute.call(el, 'data-hint-source');
    }
    return;
  }
  origSetAttribute.call(el, 'data-redfur-title', text);
  if (!el.hasAttribute('aria-label')) {
    origSetAttribute.call(el, 'aria-label', text.split('\n')[0]);
  }
  const existingHint = origGetAttribute.call(el, 'data-hint');
  const hintSource = origGetAttribute.call(el, 'data-hint-source');
  if (!existingHint || hintSource === 'title') {
    origSetAttribute.call(el, 'data-hint', text);
    origSetAttribute.call(el, 'data-hint-source', 'title');
  }
}

export function absorbAncestorTitles(target) {
  if (!target) return;
  let node = target.nodeType === 1 ? target : target.parentElement;
  while (node && node !== document.body && node.nodeType === 1) {
    const rawTitle = origGetAttribute ? origGetAttribute.call(node, 'title') : node.getAttribute('title');
    if (rawTitle !== null) {
      applyRedfurTitle(node, rawTitle);
    }
    if (node.tagName === 'svg' || node.tagName === 'SVG') {
      const svgTitles = node.querySelectorAll('title');
      for (const tNode of svgTitles) {
        const parent = tNode.parentElement;
        const tText = (tNode.textContent || '').trim();
        tNode.remove();
        if (parent && tText) {
          applyRedfurTitle(parent, tText);
        }
      }
    }
    node = node.parentElement;
  }
}

export function sweepSubtreeTitles(root = typeof document !== 'undefined' ? document.body : null) {
  if (!root || !root.querySelectorAll) return;
  const titled = root.querySelectorAll('[title]');
  for (const el of titled) {
    const raw = origGetAttribute ? origGetAttribute.call(el, 'title') : el.getAttribute('title');
    applyRedfurTitle(el, raw);
  }
  const svgTitles = root.querySelectorAll('svg title');
  for (const tNode of svgTitles) {
    const parent = tNode.parentElement;
    const tText = (tNode.textContent || '').trim();
    tNode.remove();
    if (parent && tText) {
      applyRedfurTitle(parent, tText);
    }
  }
}

let titleHookInstalled = false;
function installGlobalTitleInterceptor() {
  if (titleHookInstalled || typeof window === 'undefined' || !origSetAttribute) return;
  titleHookInstalled = true;

  Element.prototype.setAttribute = function patchedSetAttribute(name, value) {
    if (typeof name === 'string' && name.toLowerCase() === 'title') {
      applyRedfurTitle(this, value);
      return;
    }
    return origSetAttribute.call(this, name, value);
  };

  if (typeof HTMLElement !== 'undefined') {
    const desc = Object.getOwnPropertyDescriptor(HTMLElement.prototype, 'title');
    if (!desc || desc.configurable) {
      Object.defineProperty(HTMLElement.prototype, 'title', {
        configurable: true,
        enumerable: true,
        get() {
          return (
            origGetAttribute.call(this, 'data-redfur-title') ||
            origGetAttribute.call(this, 'title') ||
            ''
          );
        },
        set(val) {
          applyRedfurTitle(this, val);
        },
      });
    }
  }
}

installGlobalTitleInterceptor();

function isDelegatedRichTrigger(el) {
  if (!el || !el.classList) return false;
  return (
    el.hasAttribute('data-tip-trigger') ||
    el.classList.contains('v2-pick--item') ||
    el.classList.contains('v2-pick--user') ||
    el.classList.contains('v2-pick--guild')
  );
}

function hintFromTarget(target) {
  if (!target || !target.closest) return null;
  absorbAncestorTitles(target);
  const candidate = target.closest(
    '[data-hint], [data-tip-trigger], .v2-pick--item, .v2-pick--user, .v2-pick--guild'
  );
  if (!candidate) return null;
  if (isDelegatedRichTrigger(candidate)) {
    const explicitHint = candidate.getAttribute('data-hint');
    const isFromTitle = candidate.getAttribute('data-hint-source') === 'title';
    if (!explicitHint || isFromTitle) return null;
  }
  const el = candidate.hasAttribute('data-hint') ? candidate : target.closest('[data-hint]');
  if (!el) return null;
  const text = el.getAttribute('data-hint');
  if (!text) return null;
  const fromTitle = el.getAttribute('data-hint-source') === 'title';
  if (fromTitle) {
    if (hints.intensity() === 'off') return null;
    return { el, text };
  }
  const level = el.getAttribute('data-hint-level') || 'normal';
  if (!shouldShow(level)) return null;
  return { el, text };
}

export default function Hints() {
  installGlobalTitleInterceptor();

  let currentEl = null;
  let lastTarget = null;
  let mo = null;
  // #296 — hover-intent: defer the first show by hints.delayMs() so
  // incidental cursor traversals don't trigger a popup. Cancelled on
  // pointerleave, target change, scroll, blur, and detach.
  let intentTimer = 0;
  let intentArgs = null; // { el, text, x, y }
  let visible = false;

  function clearIntent() {
    if (intentTimer) { clearTimeout(intentTimer); intentTimer = 0; }
    intentArgs = null;
  }

  function clearCurrent() {
    currentEl = null;
    lastTarget = null;
    visible = false;
    clearIntent();
    tooltip.hide();
  }

  function fireIntent() {
    intentTimer = 0;
    if (!intentArgs) return;
    const { el, text, x, y } = intentArgs;
    intentArgs = null;
    if (!el || !el.isConnected || el !== currentEl) return;
    visible = true;
    tooltip.showAt(x, y, { content: text });
  }

  function onPointerOverCapture(ev) {
    absorbAncestorTitles(ev.target);
  }

  function onFocusInCapture(ev) {
    absorbAncestorTitles(ev.target);
  }

  function onMove(ev) {
    // #263 — if the element we were anchored to has been detached from
    // the DOM (window closed/minimised mid-hover), dismiss before any
    // hit-testing so we never carry a stale anchor across windows.
    if (currentEl && !currentEl.isConnected) {
      clearCurrent();
    }
    if (ev.target === lastTarget) {
      if (!currentEl) return;
      if (intentArgs) { intentArgs.x = ev.clientX; intentArgs.y = ev.clientY; }
      if (visible) tooltip.moveTo(ev.clientX, ev.clientY);
      return;
    }
    lastTarget = ev.target;
    const hit = hintFromTarget(ev.target);
    if (!hit) {
      if (currentEl) clearCurrent();
      return;
    }
    if (hit.el !== currentEl) {
      // New anchor — reset and start a fresh dwell timer.
      currentEl = hit.el;
      clearIntent();
      if (visible) {
        // Tooltip already up: snap-swap content for snappy element-to-
        // element transitions.
        tooltip.showAt(ev.clientX, ev.clientY, { content: hit.text });
      } else {
        const delay = Math.max(0, hints.delayMs() | 0);
        intentArgs = { el: hit.el, text: hit.text, x: ev.clientX, y: ev.clientY };
        if (delay === 0) fireIntent();
        else intentTimer = setTimeout(fireIntent, delay);
      }
    } else {
      // Same anchor — update pending coords or move the live tooltip.
      if (intentArgs) { intentArgs.x = ev.clientX; intentArgs.y = ev.clientY; }
      if (visible) tooltip.moveTo(ev.clientX, ev.clientY);
    }
  }

  function onLeave() {
    clearCurrent();
  }

  // #263 — a window root reporting pointerleave means the cursor has
  // exited that window's bounds; if the active hint anchor lives inside
  // it, drop the popup so it can't linger over the next window.
  function onWindowPointerLeave(ev) {
    if (!currentEl) return;
    const root = ev.target && ev.target.closest && ev.target.closest('.v2-window');
    if (!root) return;
    if (root.contains(currentEl)) clearCurrent();
  }

  onMount(() => {
    sweepSubtreeTitles(document.body);
    document.addEventListener('pointerover', onPointerOverCapture, { capture: true, passive: true });
    document.addEventListener('focusin', onFocusInCapture, { capture: true, passive: true });
    document.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseleave', onLeave);
    document.addEventListener('pointerleave', onWindowPointerLeave, true);
    window.addEventListener('blur', onLeave);
    if (typeof MutationObserver !== 'undefined' && document.body) {
      mo = new MutationObserver((mutations) => {
        for (const m of mutations) {
          for (const n of m.addedNodes) {
            if (n.nodeType === 1) {
              const directTitle = origGetAttribute ? origGetAttribute.call(n, 'title') : n.getAttribute('title');
              if (directTitle !== null) applyRedfurTitle(n, directTitle);
              sweepSubtreeTitles(n);
            }
          }
        }
        if (currentEl && !currentEl.isConnected) clearCurrent();
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  });
  onCleanup(() => {
    document.removeEventListener('pointerover', onPointerOverCapture, { capture: true });
    document.removeEventListener('focusin', onFocusInCapture, { capture: true });
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseleave', onLeave);
    document.removeEventListener('pointerleave', onWindowPointerLeave, true);
    window.removeEventListener('blur', onLeave);
    if (mo) { mo.disconnect(); mo = null; }
    clearCurrent();
  });

  return null;
}
